import React from 'react';
import { MapPin, Phone, Clock, Mail, Navigation, Car } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import FAQ from '../components/FAQ';
import { cabinetsFaq } from '../data/faqContent';

const Cabinets = () => {
  const cabinets = [
    {
      id: 1,
      name: "Cabinet de Vevey",
      city: "Vevey",
      image: "/images/cabinet-photo.webp",
      address: "Av. du Général-Guisan 26, 1800 Vevey",
      description: "Notre cabinet principal situé au cœur de Vevey, à proximité du lac Léman. Un cadre apaisant et professionnel pour vos consultations nutritionnelles.",
      hours: {
        weekdays: "Lundi - Vendredi : 8h00 - 18h00",
        saturday: "Samedi : 9h00 - 13h00"
      },
      access: "À 5 minutes de la gare de Vevey. Parking disponible à proximité.",
      phone: "076 779 93 02",
      email: "nutririviera@gmail.com",
      services: [
        "Consultations de nutrition",
        "Bilan nutritionnel & métabolique",
        "Impédancemétrie",
        "Nutrition hormonale & SOPK (syndrome des ovaires polykystiques)",
        "Perte de poids durable",
        "Nutrition & santé cardiovasculaire",
        "Alimentation pour la femme",
        "Équilibre émotionnel",
        "Suivi & coaching"
      ]
    },
    {
      id: 2,
      name: "Cabinet de Clarens",
      city: "Clarens",
      image: "https://images.unsplash.com/photo-1684050879098-87151a1ded14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxDbGFyZW5zJTIwTW9udHJldXglMjBsYWtlJTIwdmlldyUyMFN3aXR6ZXJsYW5kfGVufDB8fHx8MTc3NTA1NzMwMHww&ixlib=rb-4.1.0&q=85",
      address: "Rue du Grammont 2, 1815 Clarens - Montreux",
      description: "Nous vous accueillons au sein d'un cabinet médical à Clarens, dans un cadre professionnel, calme et propice à votre bien-être, idéalement situé entre Vevey et Montreux.",
      hours: {
        weekdays: "Lundi - Vendredi : 8h30 - 17h30",
        saturday: "Samedi : Sur rendez-vous"
      },
      access: "Bus : Ligne 201, Clarens centre • Train : Clarens gare • Autoroute : Sortie 15 Montreux",
      parking: "Parking CIC (nombre de places limité, payant) • Parking du Perrier (public)",
      phone: "076 779 93 02",
      email: "nutririviera@gmail.com",
      services: [
        "Consultations de nutrition",
        "Bilan nutritionnel & métabolique",
        "Impédancemétrie",
        "Nutrition hormonale & SOPK (syndrome des ovaires polykystiques)",
        "Perte de poids durable",
        "Nutrition & santé cardiovasculaire",
        "Alimentation pour la femme",
        "Équilibre émotionnel",
        "Suivi & coaching"
      ]
    }
  ];

  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Cabinets de Vevey & Clarens (Montreux) | Nutri Centre Riviera"
        description="Deux cabinets de consultation sur la Riviera vaudoise : Vevey (Av. Général-Guisan) et Clarens (Rue du Grammont). Horaires, accès, services. Proche de Montreux et Lausanne."
        keywords="cabinet nutritionniste Vevey, cabinet Clarens, cabinet Montreux, nutritionniste Riviera vaudoise, adresse Nutri Centre"
        url="https://nutricentreriviera.ch/cabinets"
      />
      {/* Hero Section */}
      <PageHero
        image="/images/cabinets-hero.webp"
        alt="Entrée lumineuse d'un cabinet de consultation premium — porte vert sauge et lumière naturelle"
        title="Nos Cabinets de Consultation"
        subtitle="Deux cabinets sur la Riviera vaudoise pour vous accompagner au plus près de chez vous"
      />

      {/* Cabinets Section */}
      <section className="section-lg" style={{ background: 'white' }}>
        <div className="container">
          <div
            className="cabinets-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '3rem',
              alignItems: 'start'
            }}
          >
            {cabinets.map((cabinet) => (
              <div
                key={cabinet.id}
                className="cabinet-card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2rem'
                }}
              >
                {/* Image */}
                <div style={{
                  position: 'relative',
                  height: '380px',
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                }}>
                  <img
                    src={cabinet.image}
                    alt={cabinet.city}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
          loading="lazy"
          decoding="async"/>
                  <div style={{
                    position: 'absolute',
                    bottom: '1.5rem',
                    left: '1.5rem',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    padding: '0.85rem 1.25rem',
                    borderRadius: '0.75rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <h3 className="heading-3" style={{ marginBottom: '0.25rem' }}>
                      {cabinet.city}
                    </h3>
                    <p className="caption" style={{ color: 'var(--accent-primary)' }}>
                      Riviera vaudoise
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
                    {cabinet.name}
                  </h2>
                  <p className="body-medium" style={{ marginBottom: '2rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                    {cabinet.description}
                  </p>

                  {/* Info Cards */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    marginBottom: '2rem'
                  }}>
                    {/* Address */}
                    <div style={{
                      display: 'flex',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'var(--bg-page)',
                      borderRadius: '0.75rem',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{
                        width: '2.5rem',
                        height: '2.5rem',
                        borderRadius: '0.5rem',
                        background: 'var(--accent-primary-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent-primary)',
                        flexShrink: 0
                      }}>
                        <MapPin size={20} />
                      </div>
                      <div>
                        <p className="body-medium" style={{ fontWeight: 500 }}>
                          {cabinet.address}
                        </p>
                        <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          {cabinet.access}
                        </p>
                      </div>
                    </div>

                    {/* Hours */}
                    <div style={{
                      display: 'flex',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'var(--bg-page)',
                      borderRadius: '0.75rem',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{
                        width: '2.5rem',
                        height: '2.5rem',
                        borderRadius: '0.5rem',
                        background: 'var(--accent-primary-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent-primary)',
                        flexShrink: 0
                      }}>
                        <Clock size={20} />
                      </div>
                      <div>
                        <p className="body-medium" style={{ fontWeight: 500 }}>
                          Horaires
                        </p>
                        <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          {cabinet.hours.weekdays}<br />
                          {cabinet.hours.saturday}
                        </p>
                      </div>
                    </div>

                    {/* Contact */}
                    {cabinet.phone && (
                      <div style={{
                        display: 'flex',
                        gap: '1rem',
                        padding: '1rem',
                        background: 'var(--bg-page)',
                        borderRadius: '0.75rem',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{
                          width: '2.5rem',
                          height: '2.5rem',
                          borderRadius: '0.5rem',
                          background: 'var(--accent-primary-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--accent-primary)',
                          flexShrink: 0
                        }}>
                          <Phone size={20} />
                        </div>
                        <div>
                          <p className="body-medium" style={{ fontWeight: 500 }}>
                            Contact
                          </p>
                          <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                            {`Tél : ${cabinet.phone}`}<br />
                            {`Email : ${cabinet.email}`}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Parking (Clarens only) */}
                    {cabinet.parking && (
                      <div style={{
                        display: 'flex',
                        gap: '1rem',
                        padding: '1rem',
                        background: 'var(--bg-page)',
                        borderRadius: '0.75rem',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{
                          width: '2.5rem',
                          height: '2.5rem',
                          borderRadius: '0.5rem',
                          background: 'var(--accent-primary-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--accent-primary)',
                          flexShrink: 0
                        }}>
                          <Car size={20} />
                        </div>
                        <div>
                          <p className="body-medium" style={{ fontWeight: 500 }}>
                            Parking
                          </p>
                          <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                            {cabinet.parking}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Services */}
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                      Services disponibles
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '0.6rem'
                    }}>
                      {cabinet.services.map((service, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.9rem'
                          }}
                        >
                          <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: 'var(--accent-primary)',
                            flexShrink: 0
                          }} />
                          <span>{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <a
                    href={cabinet.city === 'Clarens' 
                      ? "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
                      : "https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{ textDecoration: 'none' }}
                  >
                    {`Prendre rendez-vous à ${cabinet.city}`}
                    <Navigation size={20} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Responsive */}
        <style>{`
          @media (max-width: 968px) {
            .cabinets-grid {
              grid-template-columns: 1fr !important;
              gap: 4rem !important;
            }
          }
        `}</style>
      </section>

      {/* Galerie du cabinet */}
      <section className="section" style={{ background: 'var(--bg-page)', padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 3rem' }}>
            <span className="caption" style={{
              color: 'var(--accent-primary)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              Nos espaces
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Un cabinet lumineux face au lac et aux montagnes
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              Un environnement calme et chaleureux, pensé pour votre bien-être à chaque étape de votre accompagnement.
            </p>
          </div>

          <div
            className="cabinet-gallery"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr',
              gridTemplateRows: 'repeat(2, 280px)',
              gap: '1.25rem'
            }}
          >
            <div
              data-testid="cabinets-photo-1"
              style={{
                gridRow: 'span 2',
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 14px 40px rgba(20, 45, 35, 0.12)',
                background: 'var(--bg-section)'
              }}
            >
              <img
                src="/images/cabinet-vevey-2.webp"
                alt="Salle de consultation avec vue sur le Lac Léman et les montagnes — Nutri Centre Riviera, Vevey"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>

            <div
              data-testid="cabinets-photo-2"
              style={{
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 14px 40px rgba(20, 45, 35, 0.12)',
                background: 'var(--bg-section)'
              }}
            >
              <img
                src="/images/cabinet-vevey-1.webp"
                alt="Cabinet de consultation lumineux avec vue panoramique sur les Alpes — Nutri Centre Riviera"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>

            <div
              data-testid="cabinets-photo-3"
              style={{
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 14px 40px rgba(20, 45, 35, 0.12)',
                background: 'var(--bg-section)'
              }}
            >
              <img
                src="/images/cabinet-clarens.webp"
                alt="Salle d'attente épurée et accueillante — Nutri Centre Riviera, Vevey"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </div>

          <style>{`
            @media (max-width: 768px) {
              .cabinet-gallery {
                grid-template-columns: 1fr !important;
                grid-template-rows: auto !important;
                gap: 1rem !important;
              }
              .cabinet-gallery > div {
                grid-row: auto !important;
                height: 260px;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Map Section - Interactive Google Maps */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
              Nous trouver sur la Riviera
            </h2>
            <p className="body-large">
              Nos deux cabinets sont facilement accessibles entre Vevey et Montreux
            </p>
          </div>

          {/* Two Maps Side by Side */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            {/* Vevey Map */}
            <div>
              <h3 className="heading-3" style={{ marginBottom: '1rem', textAlign: 'center', color: 'var(--accent-primary)' }}>
                Cabinet de Vevey
              </h3>
              <div style={{
                position: 'relative',
                height: '350px',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                marginBottom: '1rem'
              }}>
                <iframe
                  title="Cabinet de Vevey"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2747.4!2d6.8419!3d46.4597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478ea0f3e1234567%3A0x1234567890abcdef!2sAv.%20du%20G%C3%A9n%C3%A9ral-Guisan%2026%2C%201800%20Vevey!5e0!3m2!1sfr!2sch!4v1234567890123!5m2!1sfr!2sch"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div style={{
                background: 'white',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
                  <MapPin size={18} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.2rem' }} />
                  <p className="body-small" style={{ lineHeight: '1.6' }}>
                    Av. du Général-Guisan 26, 1800 Vevey
                  </p>
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Av.+du+Général-Guisan+26,+1800+Vevey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ 
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    padding: '0.75rem 1.5rem',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <Navigation size={16} />
                  Obtenir l'itinéraire
                </a>
              </div>
            </div>

            {/* Clarens Map */}
            <div>
              <h3 className="heading-3" style={{ marginBottom: '1rem', textAlign: 'center', color: 'var(--accent-primary)' }}>
                Cabinet de Clarens
              </h3>
              <div style={{
                position: 'relative',
                height: '350px',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                marginBottom: '1rem'
              }}>
                <iframe
                  title="Cabinet de Clarens"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2746.8!2d6.9125!3d46.4420!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478ea0e1a1234567%3A0xabcdef1234567890!2sRue%20du%20Grammont%202%2C%201815%20Clarens!5e0!3m2!1sfr!2sch!4v1234567890123!5m2!1sfr!2sch"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div style={{
                background: 'white',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
                  <MapPin size={18} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.2rem' }} />
                  <p className="body-small" style={{ lineHeight: '1.6' }}>
                    Rue du Grammont 2, 1815 Clarens - Montreux
                  </p>
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Rue+du+Grammont+2,+1815+Clarens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ 
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    padding: '0.75rem 1.5rem',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <Navigation size={16} />
                  Obtenir l'itinéraire
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQ items={cabinetsFaq} heading="Questions fréquentes sur nos cabinets" />

      {/* Contact CTA */}
      <section style={{
        background: 'var(--accent-primary)',
        color: 'white',
        padding: '4rem 0'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-2" style={{ color: 'white', marginBottom: '1rem' }}>
            Questions sur nos cabinets ?
          </h2>
          <p className="body-large" style={{ 
            color: 'white', 
            opacity: 0.95, 
            maxWidth: '600px', 
            margin: '0 auto 2rem' 
          }}>
            Contactez-nous pour toute information sur nos horaires, accès ou services
          </p>
          <a
            href="/contact"
            className="btn-primary"
            style={{ 
              background: 'white', 
              color: 'var(--accent-primary)',
              textDecoration: 'none'
            }}
          >
            Nous contacter
            <Mail size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Cabinets;
